import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { CreditoCard } from "@/components/CreditoCard";
import { DashboardHero } from "@/components/DashboardHero";
import { DashboardSidebar } from "@/components/DashboardSidebar";
import { InmuebleCard } from "@/components/InmuebleCard";
import { LogoutButton } from "@/components/LogoutButton";
import { MovimientosTimeline } from "@/components/MovimientosTimeline";
import { TitularesCard } from "@/components/TitularesCard";
import { getClient } from "@/lib/api/client";
import {
  analyzeFolio,
  extractCredito,
  type CreditoInfo,
} from "@/lib/folio-analysis";
import type { ActoDetalle, Folio } from "@/lib/types";

export function FolioDetailPage() {
  const { id } = useParams<{ id: string }>();
  const client = getClient();

  const folioQuery = useQuery<Folio | null, Error>({
    queryKey: ["folio", id],
    queryFn: () => client.getFolio(id!),
    enabled: !!id,
  });

  const folio = folioQuery.data ?? null;
  const status = folio ? analyzeFolio(folio.movimientos) : null;
  const lastGravamen = status?.ultimoGravamen ?? null;

  const creditoQuery = useQuery<ActoDetalle | null, Error>({
    queryKey: [
      "acto",
      id,
      lastGravamen?.clave,
      lastGravamen?.control,
      lastGravamen?.consecutivo,
    ],
    queryFn: () =>
      client.getActoDetalle(id!, {
        clave: lastGravamen!.clave,
        control: lastGravamen!.control,
        consecutivo: lastGravamen!.consecutivo,
        fechaBoleta: lastGravamen!.fechaBoletaSirjal,
      }),
    enabled: !!folio && !!lastGravamen && !!status?.gravamenActivo,
  });

  const credito: CreditoInfo | null = creditoQuery.data
    ? extractCredito(creditoQuery.data)
    : null;

  if (folioQuery.isLoading) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50">
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
          Cargando folio…
        </div>
      </main>
    );
  }

  if (folioQuery.error) {
    const msg = folioQuery.error.message;
    const isSessionConflict = /ya ha iniciado sesi[oó]n/i.test(msg);
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-card">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-amber-50 text-amber-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6"
              aria-hidden
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" x2="12" y1="9" y2="13" />
              <line x1="12" x2="12.01" y1="17" y2="17" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-semibold text-slate-900">
            {isSessionConflict
              ? "Sesión activa en otro dispositivo"
              : "No se pudo consultar el folio"}
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            {isSessionConflict
              ? "El RPPC sólo permite una sesión por usuario. Cierra la sesión y vuelve a intentar."
              : msg.slice(0, 200)}
          </p>
          <div className="mt-6 space-y-2">
            {isSessionConflict && <LogoutButton />}
            <Link
              to="/"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Volver al buscador
            </Link>
          </div>
        </div>
      </main>
    );
  }

  if (!folio) {
    return (
      <main className="grid min-h-screen place-items-center bg-slate-50 px-6 py-12">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-card">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-rose-50 text-rose-600">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-6 w-6"
              aria-hidden
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="12" x2="12" y1="8" y2="12" />
              <line x1="12" x2="12.01" y1="16" y2="16" />
            </svg>
          </div>
          <h2 className="mt-4 text-lg font-semibold text-slate-900">
            Folio no encontrado
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            No existe un folio con el número{" "}
            <span className="font-mono text-slate-800">{id}</span> en el RPPC.
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-card transition hover:bg-indigo-500"
          >
            Volver al buscador
          </Link>
        </div>
      </main>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 lg:flex-row">
      <DashboardSidebar principal={folio.principal} />

      <main className="flex-1 px-6 py-6 lg:px-10 lg:py-10">
        <div className="mx-auto max-w-5xl space-y-6">
          <DashboardHero folio={folio} />

          {credito && <CreditoCard credito={credito} />}

          <div className="grid gap-6 lg:grid-cols-2">
            <InmuebleCard data={folio.principal} />
            <TitularesCard titulares={folio.titulares} />
          </div>

          <MovimientosTimeline
            folioId={folio.principal.folioRegistral}
            movimientos={folio.movimientos}
          />
        </div>
      </main>
    </div>
  );
}
